import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Button, Card, Form, FormControl, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader from "theme/components/common/PageHeader";
import apiClient from "../../api";
import { gettext } from "../../i18n";

// access_key is deliberately not selectable through Hasura, so it is never fetched here.
const GET_BURREL_CAMERAS = gql`
  query GetBurrelCameras {
    burrel_camera(order_by: { id: asc }) {
      id
      name
      imei
      active
    }
  }
`;

interface BurrelCamera {
  id: number;
  name: string;
  imei: string;
  active: boolean;
}

// The form carries access_key, which is write-only: blank on edit means "leave unchanged".
interface CameraForm {
  id?: number;
  name: string;
  imei: string;
  access_key: string;
  active: boolean;
}

const EMPTY_FORM: CameraForm = {
  name: "",
  imei: "",
  access_key: "",
  active: true,
};

const BurrelCamerasPage = () => {
  const { data, refetch } = useQuery<{ burrel_camera: BurrelCamera[] }>(GET_BURREL_CAMERAS);
  const [form, setForm] = useState<CameraForm | undefined>();

  const cameras = data?.burrel_camera ?? [];

  const request = async (path: string, payload: object): Promise<any | null> => {
    try {
      const resp = await apiClient.request(path, { method: "POST", data: payload });
      toast.success(gettext("Saved"));
      await refetch();
      return resp ?? {};
    } catch (e: any) {
      toast.error(gettext(e?.message?.toString() ?? "Something went wrong"));
      return null;
    }
  };

  const onSave = async () => {
    if (!form) return;
    const resp = await request("/admin/save-burrel-camera", form);
    if (!resp) return;
    setForm(undefined);
  };

  const onDelete = async (id: number) => {
    if (!window.confirm(gettext("Are you sure you want to delete this camera?"))) return;
    await request("/admin/delete-burrel-camera", { id });
  };

  const update =
    (field: keyof CameraForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => (prev ? { ...prev, [field]: e.target.value } : prev));

  const formRow = (
    <tr>
      <td />
      <td>
        <FormControl
          placeholder="Enter camera name"
          value={form?.name ?? ""}
          onChange={update("name")}
        />
      </td>
      <td>
        <FormControl
          placeholder="Enter IMEI"
          value={form?.imei ?? ""}
          onChange={update("imei")}
        />
      </td>
      <td>
        <FormControl
          placeholder={form?.id ? "New access key (optional)" : "Enter access key"}
          value={form?.access_key ?? ""}
          onChange={update("access_key")}
        />
      </td>
      <td>
        <Form.Check
          checked={form?.active ?? true}
          onChange={(e) =>
            setForm((prev) => (prev ? { ...prev, active: e.target.checked } : prev))
          }
        />
      </td>
      <td>
        <Button size="sm" onClick={onSave} className="me-1">
          Save
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setForm(undefined)}>
          Cancel
        </Button>
      </td>
    </tr>
  );

  return (
    <div>
      <PageHeader title="Burrel Cameras" className="mb-3" />

      <Card>
        <Card.Body>
          <Table responsive>
            <thead>
              <tr>
                <th>Camera ID</th>
                <th>Name</th>
                <th>IMEI</th>
                <th>Access Key</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {cameras.map((cam) =>
                form?.id === cam.id ? (
                  <React.Fragment key={cam.id}>{formRow}</React.Fragment>
                ) : (
                  <tr key={cam.id}>
                    <td>{cam.id}</td>
                    <td>{cam.name}</td>
                    <td>{cam.imei}</td>
                    <td>••••••</td>
                    <td>{cam.active ? gettext("Yes") : gettext("No")}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="sm"
                        variant="primary"
                        className="me-1"
                        style={{ minWidth: "80px" }}
                        onClick={() =>
                          setForm({
                            id: cam.id,
                            name: cam.name,
                            imei: cam.imei,
                            access_key: "",
                            active: cam.active,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        style={{ minWidth: "80px" }}
                        onClick={() => onDelete(cam.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                )
              )}

              {form && !form.id ? (
                formRow
              ) : (
                <tr>
                  <td colSpan={6}>
                    <Button size="sm" onClick={() => setForm({ ...EMPTY_FORM })}>
                      + Add camera
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BurrelCamerasPage;
